import http, { isErrorToastedByHttpClient } from '@/lib/http'
import type { CandidatePatient, DuplicateCandidate, PaginatedCandidatesResponse } from '@/types/deduplication'
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'

// ─── Fetch functions ────────────────────────────────────────────────────────

// The API returns `id_verified` and `red_flag` as '0'/'1' strings, so ternaries
// like `val ? 'True' : 'False'` previously rendered '0' as 'True'. Normalize to
// real booleans here so CandidatePatient's boolean annotation holds at runtime.
type RawCandidatePatient = Omit<CandidatePatient, 'id_verified' | 'red_flag'> & {
  id_verified: boolean | string | number | null
  red_flag: boolean | string | number | null
}

type RawDuplicateCandidate = Omit<DuplicateCandidate, 'patient_a' | 'patient_b'> & {
  patient_a: RawCandidatePatient
  patient_b: RawCandidatePatient
}

type RawPaginatedCandidatesResponse = Omit<PaginatedCandidatesResponse, 'data'> & {
  data: RawDuplicateCandidate[]
}

const toBool = (val: unknown): boolean => val === true || val === 1 || val === '1' || val === 'true'

const normalizeCandidatePatient = (raw: RawCandidatePatient): CandidatePatient => ({
  ...raw,
  id_verified: toBool(raw.id_verified),
  red_flag: toBool(raw.red_flag)
})

const normalizeDuplicateCandidate = (raw: RawDuplicateCandidate): DuplicateCandidate => ({
  ...raw,
  patient_a: normalizeCandidatePatient(raw.patient_a),
  patient_b: normalizeCandidatePatient(raw.patient_b)
})

const normalizeCandidatesResponse = (raw: RawPaginatedCandidatesResponse): PaginatedCandidatesResponse => ({
  ...raw,
  data: raw.data.map(normalizeDuplicateCandidate)
})

export type CandidatesParams = {
  page: number
  perPage: number
  sort: 'newest' | 'oldest'
  patientHash?: string
}

export const fetchMergeCandidates = async (params: CandidatesParams): Promise<PaginatedCandidatesResponse> => {
  const raw = (await http.get('/patient-deduplication/merge-candidates', {
    params: { page: params.page, per_page: params.perPage, sort: params.sort, patient_hash: params.patientHash }
  })) as RawPaginatedCandidatesResponse
  return normalizeCandidatesResponse(raw)
}

export const fetchLinkCandidates = async (params: CandidatesParams): Promise<PaginatedCandidatesResponse> => {
  const raw = (await http.get('/patient-deduplication/link-candidates', {
    params: { page: params.page, per_page: params.perPage, sort: params.sort, patient_hash: params.patientHash }
  })) as RawPaginatedCandidatesResponse
  return normalizeCandidatesResponse(raw)
}

/**
 * Mirrors NodeAPI's `mergeFieldOverridesSchema`. Anything absent there is stripped before the PHP
 * call, so a field missing here is a merge conflict the app can never resolve.
 */
export type MergeFieldOverrides = {
  first_name?: string
  last_name?: string
  date_of_birth?: string
  gender?: string
  nhs_number?: string
  mobile?: string
  email?: string

  id_verified?: boolean
  id_verified_at?: string
  idu_status?: 'REFER' | 'PASS'
  id_document_provided?: string
  id_other?: string
  mrz_code?: string
  summary_care_record?: string
  summary_care_record_updated_at?: string

  street?: string
  city?: string
  postal_code?: string
  county?: string
  country?: string
  address_note?: string

  red_flag?: boolean
  red_flag_reason?: string
  alerts?: string
  safeguarding_questions?: string

  allergies?: string
  conditions?: string
  social_history?: string
  medications?: string
  family_history?: string
  other?: string
}

type MergePatientsBody = {
  keepPatientHash: string
  fieldOverrides?: MergeFieldOverrides
}

type MergePatientsResponse = {
  code: string
  message: string
}

const mergePatientsApi = async (mergeHash: string, body: MergePatientsBody): Promise<MergePatientsResponse> => {
  return (await http.patch(`/patient-deduplication/${mergeHash}/merge`, body)) as MergePatientsResponse
}

type LinkPatientsBody = {
  basePatientHash: string
}

type LinkPatientsResponse = {
  code: string
  message: string
}

const linkPatientsApi = async (linkHash: string, body: LinkPatientsBody): Promise<LinkPatientsResponse> => {
  return (await http.patch(`/patient-deduplication/${linkHash}/link`, body)) as LinkPatientsResponse
}

type KeepSeparateBody = {
  basePatientHash: string
  groupByUser?: boolean
}

type KeepSeparateResponse = {
  code: string
  message: string
}

const keepSeparateApi = async (hash: string, body: KeepSeparateBody): Promise<KeepSeparateResponse> => {
  return (await http.patch(`/patient-deduplication/${hash}/keep-separate`, body)) as KeepSeparateResponse
}

type DuplicateCheckResponse = {
  code: string
  message: string
  pendingDuplicates: number
}

const runDuplicateCheckApi = async (patientHash: string): Promise<DuplicateCheckResponse> => {
  return (await http.post(`/patient-deduplication/${patientHash}/duplicate-check`)) as DuplicateCheckResponse
}

const invalidateDeduplicationQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['deduplication'] })
  queryClient.invalidateQueries({ queryKey: ['patient'] })
  queryClient.invalidateQueries({ queryKey: ['patients'] })
  queryClient.invalidateQueries({ queryKey: ['patient-files'] })
  queryClient.invalidateQueries({ queryKey: ['med-requests'] })
  queryClient.invalidateQueries({ queryKey: ['med-request-detail'] })
  queryClient.invalidateQueries({ queryKey: ['totalMedicationRequests'] })
  queryClient.invalidateQueries({ queryKey: ['orders'] })
  queryClient.invalidateQueries({ queryKey: ['order'] })
  queryClient.invalidateQueries({ queryKey: ['shipment-packages'] })
  queryClient.invalidateQueries({ queryKey: ['investigations'] })
  queryClient.invalidateQueries({ queryKey: ['investigation-detail'] })
  queryClient.invalidateQueries({ queryKey: ['timeline'] })
}

// ─── React Query hooks ──────────────────────────────────────────────────────

export const useMergeCandidatesQuery = (params: CandidatesParams) => {
  return useQuery({
    queryKey: ['deduplication', 'merge', params],
    queryFn: () => fetchMergeCandidates(params),
    placeholderData: keepPreviousData
  })
}

export const useLinkCandidatesQuery = (params: CandidatesParams) => {
  return useQuery({
    queryKey: ['deduplication', 'link', params],
    queryFn: () => fetchLinkCandidates(params),
    placeholderData: keepPreviousData
  })
}

// The pending-pair count PHP's `is_potential_duplicate()` gates automation on, split by queue tab.
export const usePendingDuplicatesQuery = (patientHash: string) => {
  return useQuery({
    queryKey: ['deduplication', 'pending', patientHash],
    queryFn: async () => {
      const params: CandidatesParams = { page: 1, perPage: 1, sort: 'newest', patientHash }
      const [merge, link] = await Promise.all([fetchMergeCandidates(params), fetchLinkCandidates(params)])
      return { merge: merge.total, link: link.total }
    }
  })
}

/**
 * There is no by-id endpoint, so this walks the queue a page at a time. Always pass `patientHash`
 * when it is known: it narrows the walk to that patient's pairs instead of every open one.
 */
const findCandidateById = async (
  fetcher: (params: CandidatesParams) => Promise<PaginatedCandidatesResponse>,
  groupId: number,
  patientHash?: string
): Promise<DuplicateCandidate | null> => {
  let page = 1
  while (true) {
    const res = await fetcher({ page, perPage: 100, sort: 'newest', patientHash })
    const hit = res.data.find(c => c.duplicate_id === groupId)
    if (hit) return hit
    if (page >= res.total_pages) return null
    page++
  }
}

export const useFindMergeCandidateById = (groupId: number | null, patientHash?: string) => {
  return useQuery({
    queryKey: ['deduplication', 'merge', 'by-id', groupId, patientHash],
    queryFn: () => findCandidateById(fetchMergeCandidates, groupId as number, patientHash),
    enabled: groupId != null
  })
}

export const useFindLinkCandidateById = (groupId: number | null, patientHash?: string) => {
  return useQuery({
    queryKey: ['deduplication', 'link', 'by-id', groupId, patientHash],
    queryFn: () => findCandidateById(fetchLinkCandidates, groupId as number, patientHash),
    enabled: groupId != null
  })
}

export const useMergePatientsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ mergeHash, body }: { mergeHash: string; body: MergePatientsBody }) =>
      mergePatientsApi(mergeHash, body),
    onSuccess: () => {
      invalidateDeduplicationQueries(queryClient)
    }
  })
}

export const useLinkPatientsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ linkHash, body }: { linkHash: string; body: LinkPatientsBody }) => linkPatientsApi(linkHash, body),
    onSuccess: () => {
      invalidateDeduplicationQueries(queryClient)
    },
    onError: () => {
      enqueueSnackbar('Failed to link patients. Please try again.', { variant: 'error' })
    }
  })
}

export const useKeepSeparateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ hash, body }: { hash: string; body: KeepSeparateBody }) => keepSeparateApi(hash, body),
    onSuccess: () => {
      invalidateDeduplicationQueries(queryClient)
    },
    onError: () => {
      enqueueSnackbar('Failed to mark patient as separate. Please try again.', { variant: 'error' })
    }
  })
}

export const useRunDuplicateCheckMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (patientHash: string) => runDuplicateCheckApi(patientHash),
    onSuccess: async ({ pendingDuplicates }) => {
      await queryClient.invalidateQueries({ queryKey: ['deduplication'] })
      if (pendingDuplicates > 0) {
        const noun = pendingDuplicates === 1 ? 'potential duplicate' : 'potential duplicates'
        enqueueSnackbar(`Duplicate check complete: ${pendingDuplicates} ${noun} to review`, { variant: 'warning' })
      } else {
        enqueueSnackbar('Duplicate check complete: no potential duplicates found', { variant: 'success' })
      }
    },
    onError: error => {
      if (!isErrorToastedByHttpClient(error)) {
        enqueueSnackbar('Could not run the duplicate check. Please try again.', { variant: 'error' })
      }
    }
  })
}
